import { treaty } from "@elysiajs/eden";
import type { App } from "@worker/app";
import type { DailyPrice, FxRate, Instrument, Plan, User } from "./types";

export const eden = treaty<App>(window.location.origin);

const dateOnly = (value: unknown) => value instanceof Date ? value.toISOString().slice(0, 10) : String(value).slice(0, 10);
const dateTime = (value: unknown) => value instanceof Date ? value.toISOString() : String(value);
const normalizeInstrument = (instrument: Instrument): Instrument => ({
  ...instrument,
  backfillStartDate: dateOnly(instrument.backfillStartDate),
  validatedAt: instrument.validatedAt ? dateTime(instrument.validatedAt) : null,
  createdAt: dateTime(instrument.createdAt),
  updatedAt: dateTime(instrument.updatedAt),
});
const normalizePlan = (plan: Plan): Plan => ({
  ...plan,
  instrument: normalizeInstrument(plan.instrument),
  lastOpenedAt: dateTime(plan.lastOpenedAt),
  createdAt: dateTime(plan.createdAt),
  updatedAt: dateTime(plan.updatedAt),
});

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

async function unwrap<T>(resultPromise: Promise<unknown>): Promise<T> {
  const result = (await resultPromise) as {
    data: T | null;
    error: null | { status: number; value?: { error?: { code?: string; message?: string; details?: Record<string, unknown> } } };
  };
  if (result.error) {
    const payload = result.error.value?.error;
    throw new ApiClientError(result.error.status, payload?.code ?? "REQUEST_FAILED", payload?.message ?? "Request failed.", payload?.details);
  }
  return result.data as T;
}

export const api = {
  me: () => unwrap<{ user: User | null }>(eden.api.me.get()),
  logout: () => unwrap<{ ok: true }>(eden.api.auth.logout.post({})),
  instruments: async () => {
    const result = await unwrap<{ instruments: Instrument[] }>(eden.api.instruments.get());
    return { instruments: result.instruments.map(normalizeInstrument) };
  },
  prices: async (instrumentId: string, range: "1M" | "3M" | "6M" | "1Y" | "ALL") => {
    const result = await unwrap<{ instrument: Instrument; prices: DailyPrice[] }>(eden.api.instruments({ id: instrumentId }).prices.get({ query: { range } }));
    return {
      instrument: normalizeInstrument(result.instrument),
      prices: result.prices.map((price) => ({ ...price, tradeDate: dateOnly(price.tradeDate), fetchedAt: dateTime(price.fetchedAt) })),
    };
  },
  fx: async () => {
    const result = await unwrap<{ rate: FxRate }>(eden.api.fx["usd-thb"].latest.get());
    return { rate: result.rate ? { ...result.rate, tradeDate: dateOnly(result.rate.tradeDate) } : null };
  },
  plans: async () => {
    const result = await unwrap<{ plans: Plan[] }>(eden.api.plans.get());
    return { plans: result.plans.map(normalizePlan) };
  },
  createPlan: async (body: { name: string; instrumentId: string }) => {
    const result = await unwrap<{ plan: Plan }>(eden.api.plans.post(body));
    return { plan: normalizePlan(result.plan) };
  },
  updatePlan: (id: string, body: Partial<Plan> & { version: number }) =>
    unwrap<{ plan: Plan }>(eden.api.plans({ id }).patch(body)).then((result) => ({ plan: normalizePlan(result.plan) })),
  openPlan: (id: string) => unwrap<{ ok: true }>(eden.api.plans({ id }).open.post({})),
  duplicatePlan: (id: string, name?: string) => unwrap<{ plan: Plan }>(eden.api.plans({ id }).duplicate.post({ name })).then((result) => ({ plan: normalizePlan(result.plan) })),
  deletePlan: (id: string, version: number) => unwrap<{ ok: true }>(eden.api.plans({ id }).delete({ version })),
  exportPlans: () => unwrap<Record<string, unknown>>(eden.api.plans.export.get()),
  importLegacy: (payload: unknown) => unwrap<{ imported: string[]; skipped: string[] }>(eden.api.plans.import.post(payload)),
  adminUsers: () => unwrap<{ users: Array<User & { approvedAt: string | null; lastLoginAt: string; createdAt: string }> }>(eden.api.admin.users.get()),
  updateUser: (id: string, status: "ACTIVE" | "SUSPENDED") => unwrap<{ ok: true }>(eden.api.admin.users({ id }).patch({ status })),
  adminInstruments: async () => {
    const result = await unwrap<{ instruments: Instrument[] }>(eden.api.admin.instruments.get());
    return { instruments: result.instruments.map(normalizeInstrument) };
  },
  createInstrument: (body: {
    symbol: string; exchange: string; providerSymbol: string; displayName: string;
    currency: string; timezone: string; backfillStartDate: string;
  }) => unwrap<{ instrument: Instrument }>(eden.api.admin.instruments.post(body)),
  updateInstrument: (id: string, active: boolean) => unwrap<{ ok: true }>(eden.api.admin.instruments({ id }).patch({ active })),
  syncInstrument: (id: string, backfill = false) => unwrap<{ queued: true }>(eden.api.admin.instruments({ id }).sync.post({ backfill })),
  syncFx: () => unwrap<{ queued: true }>(eden.api.admin.fx.sync.post({})),
  syncRuns: () => unwrap<{ runs: Array<Record<string, unknown>> }>(eden.api.admin["sync-runs"].get({ query: { limit: 50 } })),
};
